import 'core-js/es/array/includes';
import 'core-js/es/array/from';
import 'core-js/es/object/assign';
import 'core-js/es/number';
import focusWithin from 'ally.js/esm/style/focus-within';
import FontFaceObserver from 'fontfaceobserver';
import mobile from 'is-mobile';
import debounce from 'lodash/debounce';
import 'objectFitPolyfill';
import 'promise-polyfill/src/polyfill';
import smoothscroll from 'smoothscroll-polyfill';
import './base/polyfill';
import {isIE, scrollWithOffset, triggerEvent, wrapInner} from './base/utils';
import A11y from './components/a11y';
import Cookies from './components/cookies';
import Modal from './components/modal';
import Newsletter from './components/newsletter';
import OpenHours from './components/open-hours';
import Orphans from './components/orphans';
import TicketAvailability from './components/ticket-availability';
import TopBar from './components/top-bar';

class App {
    constructor() {
        this.loadFonts();

        for (let el of document.querySelectorAll('.text, .orphans')) {
            new Orphans(el);
        }

        this.checkMobile();
        window.addEventListener('resize', debounce(this.checkMobile.bind(this), 100));

        this.init();
        setTimeout(() => { // deferred script runs before CSSOM
            document.body.classList.remove('preload');
        }, 10);
    }

    loadFonts() {
        let font = new FontFaceObserver('museo-sans');

        font.load().then(function() {
            document.documentElement.className += ' fonts-loaded';
        });
    }

    init() {
        smoothscroll.polyfill();
        if (typeof lazyLoadInstance !== 'undefined') {
            lazyLoadInstance.update();
        }
        focusWithin();

        new TopBar(document.querySelector('.header'));
        new Newsletter(document.querySelector('.newsletter'));
        new OpenHours();
        new Cookies();

        this.initModule('.js-custom-select, .js-tabs-dropdown, .form select', 'custom-select');
        this.initModule('.alert', 'alert');
        this.initModule('.gallery', 'gallery');
        this.initModule('.accordion', 'accordion');
        this.initModule('.video-banner', 'video-popup');
        this.initModule('.timeline', 'timeline');
        this.initModule('.custom-side-menu ', 'side-custom-menu');
        this.initModule('.js-search-results-wrap', 'main-search');
        this.initModule('.js-kb-search-results-wrap', 'knowledge-base-search');
        this.initModule('.hp-hero', 'hp-hero');
        this.initModule('.article-sidebar-container', 'article-sidebar');
        this.initModule('.video-player', 'video-player');
        this.initModule('.centre-plan', 'centre-plan');
        this.initModule('.program', 'program');
        this.initModule('.faq', 'faq-search');

        this.initLinks();
        this.initSkiplinks();
        this.initTabs();
        this.initTables();
        this.initGoToTop();
        this.initAnchors('.text [href*="#"], .box [href*="#"], .hub [href*="#"]');
        this.initAvailability();
        this.initNewsList();
        this.initProcurementList();
        this.initSliders();
        this.initWebform();

        A11y.abbr();

        this.detectOldBrowser();
    }

    checkMobile() {
        if (mobile({tablet: true, featureDetect: true})) {
            document.body.classList.add('mobile');
        } else {
            document.body.classList.remove('mobile');
        }
    }

    detectOldBrowser() {
        if (isIE()) {
            document.body.classList.add('browser-ie');
        }
    }

    initGoToTop() {
        let $goToTop = document.querySelector('.js-go-to-top');

        if ($goToTop) {
            $goToTop.addEventListener('click', e => {
                e.preventDefault();
                window.scroll({top: 0, left: 0, behavior: 'smooth'});
            });
        }
    }

    initLinks($context = document) {
        window.addEventListener('load', () => {
            for (let el of $context.querySelectorAll('.text a[href]:not(.btn):not(.file-block), .link, .form a:not(.btn)')) {
                if (el.children.length === 0) {
                    wrapInner(el, 'span', 'class', 'underline');
                } else {
                    el.classList.add('no-underline');
                }
            }
        });
    }

    initAnchors(items) {
        for (let el of document.querySelectorAll(items)) {
            if (el.hash.length < 2)
                continue;

            // noinspection TypeScriptValidateTypes
            let hash = el.hash.split('#')[1],
                id = hash.split(',')[0],
                $targetById = document.getElementById(id),
                $target = $targetById ? $targetById : document.querySelector(`[data-id="${id}"]`);

            if (!$target)
                continue;

            if ($target.classList.contains('modal')) {
                new Modal(el, hash);
            } else {
                el.addEventListener('click', e => {
                    e.preventDefault();
                    e.stopPropagation();

                    scrollWithOffset($target).then(() => {
                        triggerEvent($target, 'anchor');
                    });
                });
            }
        }
    }

    initSkiplinks() {
        let $skiplinks = document.querySelectorAll('.skiplinks a');

        if ($skiplinks.length) {
            for (let $s of $skiplinks) {
                $s.addEventListener('click', e => {
                    e.preventDefault();

                    let id = e.target.href.split('#')[1],
                        focus = e.target.dataset.focus;

                    if (id) {
                        focus = focus || `#${id}`;

                        scrollWithOffset(document.getElementById(id));
                        document.querySelector(focus).focus();
                    }
                });
            }
        }
    }

    initTabs() {
        let $tabs = document.querySelectorAll('.tabs-wrap');

        if ($tabs.length) {
            import(/* webpackChunkName: "tabs" */ `./components/tabs.js`)
                .then(({default: Module}) => {
                    new Module($tabs);
                })
                .catch(error => console.warn('An error occurred while loading the component', error));
        }
    }

    initTables() {
        for (let $table of document.querySelectorAll('.text table')) {
            let $wrap = document.createElement('div'),
                bodyRow = $table.querySelector('tbody tr'),
                bodyHeadings = bodyRow.querySelectorAll('th');

            // mark last header column
            if (bodyHeadings.length) {
                let lastCol = bodyHeadings.length - 1,
                    headRows = $table.querySelectorAll('thead tr');

                for (let $row of headRows) {
                    $row.children[lastCol].classList.add('last-th');
                }
            }

            // add a scrollable wrapper
            $wrap.className = 'table-wrap';
            $table.insertAdjacentElement('afterend', $wrap);
            $wrap.insertAdjacentElement('afterbegin', $table);
        }
    }

    initAvailability() {
        let availability = new TicketAvailability(document.querySelector('.availability')),
            $availabilityBtn = document.querySelector('.js-ticket-availability');

        if ($availabilityBtn) {
            new Modal($availabilityBtn, 'ticket-availability', {
                onOpen: () => {
                    availability.load();
                }
            });
        }
    }

    initModule(selector, moduleName, params = []) {
        let $element = document.querySelectorAll(selector);

        if ($element.length) {
            import(/* webpackChunkName: "[request]" */ `./components/${moduleName}.js`)
                .then(({default: Module}) => {
                    for (let $el of $element) {
                        new Module($el, ...params);
                    }
                })
                .catch(error => console.warn('An error occurred while loading the component', error));
        }
    }

    initSliders() {
        let $articlesSlider = document.querySelectorAll('.articles-slider'),
            $eventsSlider = document.querySelectorAll('.events-slider'),
            $boxSlider = document.querySelectorAll('.box-list.mobile-slider:not(.small-box-list):not(.program-other-attractions)'),
            $smallBoxSlider = document.querySelectorAll('.small-box-list.mobile-slider .slider'),
            $supportSlider = document.querySelectorAll('.support-slider'),
            $supportSectionSlider = document.querySelectorAll('.support-section.mobile-slider'),
            $caseSlider = document.querySelectorAll('.case-slider'),
            $recommendationsSlider = document.querySelectorAll('.recommendations-slider'),
            $otherAttractionsSlider = document.querySelectorAll('.program-other-attractions'),
            $eventsMapSlider = document.querySelectorAll('.events-map-slider');

        if ($articlesSlider || $eventsSlider || $boxSlider || $smallBoxSlider || $supportSlider
            || $supportSectionSlider || $caseSlider || $recommendationsSlider || $otherAttractionsSlider || $eventsMapSlider) {
            import(/* webpackChunkName: "slider" */ `./components/slider.js`)
                .then(({default: Slider}) => {
                    for (let el of $articlesSlider) { this.initArticlesSlider(Slider, el); }
                    for (let el of $eventsSlider) { this.initEventsSlider(Slider, el); }
                    for (let el of $boxSlider) { this.initBoxSlider(Slider, el); }
                    for (let el of $smallBoxSlider) { this.initSmallBoxSlider(Slider, el); }
                    for (let el of $supportSlider) { this.initSupportSlider(Slider, el); }
                    for (let el of $supportSectionSlider) { this.initSupportSectionSlider(Slider, el); }
                    for (let el of $caseSlider) { this.initCaseSlider(Slider, el); }
                    for (let el of $recommendationsSlider) { this.initRecommendationsSlider(Slider, el); }
                    for (let el of $otherAttractionsSlider) { this.initOtherAttractionsSlider(Slider, el); }
                    for (let el of $eventsMapSlider) { this.initEventsMapSlider(Slider, el); }
                })
                .catch(error => console.warn('An error occurred while loading the component', error));
        }
    }

    initArticlesSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 1,
            spaceBetween: 30,
            loop: false,
            autoHeight: true,
            navigation: true,
            focusable: 'a',
            breakpoints: {
                768: {
                    slidesPerView: 1.4,
                    spaceBetween: 60
                },
                1024: {
                    slidesPerView: 2.4,
                    spaceBetween: 60
                }
            }
        });
    }

    initEventsSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 1,
            spaceBetween: 20,
            loop: false,
            autoHeight: true,
            navigation: true,
            focusable: 'a',
            breakpoints: {
                768: {
                    slidesPerView: 2,
                    spaceBetween: 60
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 60
                },
                1280: {
                    slidesPerView: 4,
                    spaceBetween: 60
                }
            }
        });
    }

    initBoxSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 1.3,
            spaceBetween: 0,
            loop: false,
            navigation: false,
            wrapperClass: 'box-list-wrap',
            slideClass: 'box',
            focusable: 'a',
            breakpoints: {
                768: {
                    disable: true
                }
            }
        });
    }

    initSmallBoxSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 'auto',
            spaceBetween: 0,
            loop: false,
            navigation: true,
            wrapperClass: 'box-list-wrap',
            slideClass: 'box',
            focusable: 'a',
            breakpoints: {
                768: {
                    disable: true
                }
            }
        });
    }

    resizeButtons(Slider, $slides, $buttons) {
        let maxH = Array.from($buttons).map(el => el.offsetHeight)
            .reduce((acc, curr) => Math.max(acc, curr), 0);

        for (let el of $slides) {
            el.style.paddingBottom = maxH + 'px';
        }
    }

    initEventsMapSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 1,
            spaceBetween: 60,
            loop: false,
            navigation: true,
            breakpoints: {
                768: {
                    slidesPerView: 2
                },
                1024: {
                    slidesPerView: 3
                }
            },
            on: {
                init: this.prepareMapLinks($slider)
            }
        });
    }

    prepareMapLinks($slider) {
        const sliderId = $slider.getAttribute('data-id'),
              $slides = $slider.querySelectorAll('.events-map-slide');

        for (let el of $slides) {
            const map = el.getAttribute('data-map'),
                  polygon = el.getAttribute('data-polygon'),
                  $links = el.querySelectorAll('a:not(.btn)'),
                  $mapLinks = el.querySelectorAll('.events-slider-map-link');

            if ($links.length) {
                for (let el of $links) {
                    wrapInner(el, 'span', 'class', 'underline');
                }
            }

            if ($mapLinks.length && map) {
                for (let el of $mapLinks) {
                    el.setAttribute('href', `#location,location-${map}` + (polygon.length ? `,${polygon}` : ''));
                }
            }
        }

        this.initAnchors(`[data-id="${sliderId}"] [href*="#"]`);
    }

    initSupportSlider(Slider, $slider) {
        let $slides = $slider.querySelectorAll('.support-slide');
        let $buttons = $slider.querySelectorAll('.support-slide-buttons');

        new Slider($slider, {
            slidesPerView: 1,
            spaceBetween: 60,
            loop: false,
            navigation: true,
            breakpoints: {
                768: {
                    slidesPerView: 2
                },
                1024: {
                    slidesPerView: 3
                }
            },
            on: {
                init: this.resizeButtons.bind(this, $slides, $buttons),
                resize: this.resizeButtons.bind(this, $slides, $buttons)
            }
        });
    }

    initCaseSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 'auto',
            loop: false,
            navigation: true,
            focusable: 'a',
            on: {
                init: this.initAnchors('.case-slide [href*="#"]')
            }
        });
    }

    initSupportSectionSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 1.5,
            spaceBetween: 60,
            loop: false,
            navigation: false,
            centerInsufficientSlides: true,
            wrapperClass: 'support-section-items',
            slideClass: 'support-section-item',
            breakpoints: {
                768: {
                    disable: true
                }
            }
        });
    }

    initRecommendationsSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 1,
            spaceBetween: 30,
            loop: false,
            autoHeight: false,
            navigation: true,
            focusable: 'a',
            breakpoints: {
                768: {
                    slidesPerView: 1.3,
                    spaceBetween: 0
                },
                1024: {
                    slidesPerView: 2.3,
                    spaceBetween: 0
                }
            }
        });
    }

    initOtherAttractionsSlider(Slider, $slider) {
        new Slider($slider, {
            slidesPerView: 1,
            // spaceBetween: 30,
            loop: false,
            autoHeight: false,
            navigation: true,
            wrapperClass: 'box-list-wrap',
            slideClass: 'box',
            focusable: 'a',
            breakpoints: {
                768: {
                    slidesPerView: 2.5
                },
                1024: {
                    disable: true
                }
            }
        });
    }

    initNewsList() {
        let $newsList = document.querySelectorAll('.news-list');

        if (!$newsList.length) {
            return;
        }

        import(/* webpackChunkName: "archive" */ `./components/archive.js`)
            .then(({default: Archive}) => {
                new Archive({
                    parse: ($item, data) => {
                        if (!data.img) {
                            $item.querySelector('.js-img').remove();
                        }
                    }
                });
            })
            .catch(error => console.warn('An error occurred while loading the component', error));
    }

    initProcurementList() {
        let $newsList = document.querySelectorAll('.procurement-list'),
            $filter, $month, available, months;

        if (!$newsList.length) {
            return;
        }

        $filter = document.querySelector('.js-archive-filter');
        $month = $filter.querySelector('[name="month"]');
        try {
            available = JSON.parse($filter.dataset['available']);
            months = JSON.parse($filter.dataset['months']);
        } catch (e) {
            console.error('Malformed months array');
        }

        import(/* webpackChunkName: "archive" */ `./components/archive.js`)
            .then(({default: Archive}) => {
                new Archive({
                    change: e => {
                        if (e.target.name === 'year') {
                            let availableMonthsInYear = available[e.target.value];

                            triggerEvent($month, 'set', months.filter(el => { return availableMonthsInYear.includes(el.value); }));
                        }
                    }
                });
            })
            .catch(error => console.warn('An error occurred while loading the component', error));
    }

    webformDeleteFiles($context) {
        let $deleteFileCheckbox = $context.querySelectorAll('.webform-submission-form .managed-file .form-checkbox'),
            $deleteFileButton = $context.querySelector('.webform-submission-form .managed-file [name="file_remove_button"]');

        if ($deleteFileCheckbox && $deleteFileButton) {
            for (let el of $deleteFileCheckbox) {
                el.addEventListener('click', () => {
                    triggerEvent($deleteFileButton, 'mousedown');
                });
            }
        }
    }

    webformFocusLabel($context = document) {
        let $fileButton = $context.querySelector('.webform-file-button-input');

        if ($fileButton) {
            $fileButton.addEventListener('focus', () => {
                let $fileButtonLabel = $fileButton.closest('.form-managed-file').querySelector('label');

                $fileButtonLabel.className += ' ally-focus-within';
            });
        }
    }

    initWebform() {
        let app = this,
            $form = document.querySelector('.webform-submission-form'),
            $selects = document.querySelectorAll('.webform-submission-form select');

        if ($form) {
            this.webformFocusLabel();
        }

        /* global Drupal */
        if ($form && $selects && typeof (Drupal) !== 'undefined') {
            import(/* webpackChunkName: "custom-select" */ `./components/custom-select.js`)
                .then(({default: CustomSelect}) => {
                    document.body.addEventListener('refresh', e => {
                        let $selectNotChoices = e.target.querySelector('select:not(.choices__input)');

                        if ($selectNotChoices && !$selectNotChoices.parentElement.classList.contains('custom-select-native')) {
                            new CustomSelect($selectNotChoices);
                        }
                    });
                })
                .catch(error => 'An error occurred while loading the component');

            Drupal.behaviors.testBehavior = {
                attach: function(context) {
                    let $select = context.querySelectorAll('.js-form-type-select'),
                        $popupClose = context.querySelector('.popup-close'),
                        errorField = context.querySelector('.form-item--error'),
                        managedFileErrorField = context.querySelector('.managed-file.form-item--error'),
                        ajaxNewContent = context.querySelector('.ajax-new-content'),
                        thankyou = context.querySelector('.webform-confirmation');

                    for (let $s of $select) {
                        triggerEvent($s, 'refresh');
                    }

                    app.webformDeleteFiles(context);
                    app.webformFocusLabel(context);
                    app.initLinks(context);

                    if (!context.querySelector('.managed-file .webform-file-button') && context.querySelector('.managed-file .description')) {
                        context.querySelector('.managed-file .description').className += ' hidden';
                    }

                    if ($popupClose) {
                        $popupClose.addEventListener('click', () => {
                            triggerEvent(document.body, 'modal-close');
                        });
                    }

                    if (errorField && !ajaxNewContent) {
                        let input = errorField.querySelector('input:not([disabled]), textarea:not([disabled]), select:not([disabled])'),
                            modal = errorField.closest('.modal-inner') || document.body,
                            title = modal.querySelector('.modal-title');

                        scrollWithOffset(errorField, modal, title).then(() => {
                            if (errorField.classList.contains('select')) {
                                let styledSelect = input.closest('.choices');

                                if (styledSelect) {
                                    styledSelect.focus();
                                } else {
                                    input.focus();
                                }
                            } else {
                                input.focus();
                            }
                        });
                    } else if (managedFileErrorField && ajaxNewContent) {
                        let modal = managedFileErrorField.closest('.modal-inner') || document.body,
                            title = modal.querySelector('.modal-title');

                        scrollWithOffset(managedFileErrorField, modal, title);
                    } else if (thankyou) {
                        let modal = thankyou.closest('.modal-inner') || document.body,
                            title = modal.querySelector('.modal-title');

                        scrollWithOffset(thankyou, modal, title);
                    }
                }
            };
        }

        document.body.addEventListener('modal-closed', e => {
            let $webform = e.detail ? e.detail.querySelector('form') : null;

            if ($webform) {
                $webform.reset();

                for (let $s of $webform.querySelectorAll('select')) {
                    triggerEvent($s, 'select', '');
                }

                if ($webform.querySelectorAll('.managed-file .form-checkbox')) {
                    for (let el of $webform.querySelectorAll('.managed-file .form-checkbox')) {
                        el.checked = true;
                    }
                }

                if ($webform.querySelector('.managed-file [name="file_remove_button"]')) {
                    triggerEvent($webform.querySelector('.managed-file [name="file_remove_button"]'), 'mousedown');
                }
            }
        });
    }
}

document.addEventListener('DOMContentLoaded', () => new App());
